import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule }
	from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { DialogResponse, ExceptionReportRequest,
	ExceptionReportToDisplay } from '../../../../order/models/exception-report';
import { Order } from '../../../../order/models/order';
import { ExceptionReportService } from '../../../../order/services/exception-report.service';
import { pageTransitions } from '../../../animations/page-transitions';
import { TranslationService } from '../../../services/translation.service';
import { DimensionPipe } from '../../../pipes/format-dimension.pipe';
import { ModelInfoPipe } from '../../../pipes/model-info.pipe';
import { OrderService } from '../../../../order/services/order.service';

@Component({
	selector: 'copo-pin-delete',
	templateUrl: './dialog-pin.component.html',
	styleUrl: './dialog-pin.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatDialogModule,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		TextFieldModule,
		MatIconModule,

		MatButtonModule,
		ModelInfoPipe,
		DimensionPipe,
	],
})
export class DialogPinComponent {
	exceptionForm: FormGroup = new FormGroup({
		comment: new FormControl({ value: '', disabled: false }),
	});

	buttonName = 'issue.send.claim';
	isUpdate = false;
	isEditView = false;
	exceptionReport?: ExceptionReportToDisplay;
	titleText = 'exception-report.dialog.current-status.title';

	isDisabled = false;
	warningText = 'warning.closed.orders';
	constructor(

		@Inject(MAT_DIALOG_DATA) public order: Order,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly exceptionReportService: ExceptionReportService,

		private readonly dialogRef: MatDialogRef<DialogPinComponent>,
		private readonly orderService: OrderService
	) {
		this.exceptionReport = order.exceptionReport;
		this.buildDialogScreenView();
	}

	buildDialogScreenView() {
		if (!this.exceptionReport) {
			return;
		}
		const formattedWeek = this.exceptionReportService.getYearWeek();
		if (this.exceptionReport.yearWeek === formattedWeek && this.exceptionReport.commentFromCustomer) {
			this.buttonName = 'dashboard.savechanges';
			this.isDisabled = true;
			this.exceptionForm.patchValue({
				comment: this.exceptionReport.commentFromCustomer,
			});
			this.exceptionForm.get('comment')?.disable();
			this.titleText = 'exception-report.dialog.current-status.title';
			this.isEditView = true;
		} else {
			this.buttonName = 'issue.send.claim';
			this.isDisabled = false;
			this.exceptionForm.get('comment')?.enable();
			this.titleText = 'exception-report.dialog.add-comment.title';
			this.isEditView = false;
		}
	}

	updateView() {
		this.exceptionForm.get('comment')?.enable();
		this.isUpdate = true;
	}

	submitAction() {
		if (!this.exceptionReport && this.isEditView) {
			return;
		}
		this.addOrUpdateComment(<string> this.exceptionForm.controls['comment'].value);
	}

	addOrUpdateComment(comment: string) {
		const exceptionRequests: ExceptionReportRequest[] = [];
		const yearWeekToSave = this.exceptionReportService.getYearWeek();
		if (!this.isUpdate) {
			const requestBody: ExceptionReportRequest = {
				docNo: `${ this.order.supplierOrderNumber }/${ this.order.supplierOrderItemNumber }`,
				yearWeek: yearWeekToSave,
				comment,
				origin: 'Client'
			};
			exceptionRequests.push(requestBody);
		} else {
			if (!this.exceptionReport) {
				return;
			}

			const requestBody: ExceptionReportRequest = {
				docNo: this.exceptionReport.docNo,
				yearWeek: this.exceptionReport.yearWeek,
				comment,
				origin: 'Client',
			};
			exceptionRequests.push(requestBody);
		}

		this.exceptionReportService.addOrUpdateComment(exceptionRequests).then(() => {
			const result: DialogResponse = {
				action: 'submit',
				isDeletion: false,
				data: exceptionRequests[0]
			};
			this.dialogRef.close(result);
			this.snackBar.open(
				this.translationService.getTR('exception-report.save'),
				undefined,
				{
					duration: 5000,
					panelClass: ['custom-snackbar']
				},
			);
		}).catch((error: unknown) => {
			console.error('error while adding comment', error);
		});
	}

	deleteComment() {
		if (!this.exceptionReport) {
			return;
		}
		if (!this.exceptionReport.yearWeek) {
			return;
		}
		const exceptionRequest: ExceptionReportRequest = {
			yearWeek: this.exceptionReport.yearWeek,
			comment: this.exceptionReport.commentFromCustomer ?? '',
			docNo: this.exceptionReport.docNo,
			origin: 'Client'
		};
		this.exceptionReportService.deleteComment(
			`${ this.order.supplierOrderNumber }/${ this.order.supplierOrderItemNumber }`,
			'Client',
			this.exceptionReport.yearWeek
		).then(() => {
			const result = {
				action: 'submit',
				isDeletion: true,
				data: exceptionRequest
			};
			this.dialogRef.close(result);
			this.snackBar.open(
				this.translationService.getTR('exception-report.delete'),
				undefined,
				{
					duration: 5000,
					panelClass: ['custom-snackbar']
				},
			);
		}).catch((error: unknown) => {
			console.error('error while deleting comment', error);
		});
	}

	checkCddWeekData() {
		return this.orderService.checkCddWeekData(this.order.cddWeek);
	}
}
